import { createAsyncThunk, createSlice, PayloadAction, } from "@reduxjs/toolkit";
import { replace } from 'connected-react-router';
import axios from 'axios';
import { AppDispatch, RootState } from "../../app/store";
import { CxQuestion, CxQuestionType, CX_SCORE_VAL, BranchesRes } from "../../entity/entities";
import { CxAnswerPageDto } from "../../../../user-api/dist/src/dto/answer-page.dto";
import { appPath } from "../../router/AppRoute";
import { APP_MODE_V, CxHttpRes } from '../../../../user-api/dist/src/dto/shopqr/shopqr-session-dto';
import { AssertUtil } from "../../util/assert-util";
import { ClientInfoUtil } from "../../util/client-info-util";
import { CxAnswerDto, CxAnswerSet } from "../../../../user-api/dist/src/dto/answer.dto";
import { CxQuestionOption } from "../../../../user-api/dist/src/entity/cx-question-option.entity";
import { ZIPCODE_REGEX } from '../../Constants';

export const REQ_STATUS_V = {
  idle: 'idle',
  pending: 'pending',
  fulfilled: 'fulfilled',
  rejected: 'rejected',
} as const;

export type REQ_STATUS = typeof REQ_STATUS_V[keyof typeof REQ_STATUS_V];

// ================================================================================================
// State begin
interface InputAnswerState {
  session: {
    status: REQ_STATUS,
    cxHttpRes: null | CxHttpRes,
    httpResIsValid: boolean,
    couponInStock: boolean,
  },
  errorCount: number,
  couponState: {
    // imageUrl: string,
    // needsEmail: boolean,
    registerEmailStatus: REQ_STATUS,
    issueCouponStatus: REQ_STATUS,
    useCouponStatus: REQ_STATUS,
  },
  answer: {
    status: REQ_STATUS,
    cxAnswerPageDtos: CxAnswerPageDto[],
  },
  selectedBranch: BranchesRes | null,
}

const initialState: InputAnswerState = {
  session: {
    status: 'idle',
    cxHttpRes: null as null | CxHttpRes,
    httpResIsValid: false,
    couponInStock: false,
  },
  errorCount: 0,
  couponState: {
    registerEmailStatus: 'idle',
    issueCouponStatus: 'idle',
    useCouponStatus: 'idle',
  },
  answer: {
    status: 'idle',
    cxAnswerPageDtos: [],
  },
  selectedBranch: null,
}
// State end
// ================================================================================================


// interface ResDataStartSession {
//   appSession: AppSession,
//   shopToken: ShopToken,
// }

// interface HttpRes {
//   isError: boolean,
//   messages: {
//     isError: boolean,
//     message: string,
//   }[],
//   isTest: boolean,
//   data: null|ResDataStartSession,
// }

// ================================================================================================
// thunk begin

// 初回起動時に呼び出す
// サーバ側の情報と同期を取る
export const startServerSession = createAsyncThunk<
  CxHttpRes,
  undefined,
  {
    dispatch: AppDispatch,
    state: RootState,
  }
>('inputAnswer/startServerSession', async () => {

  const response = await axios.post(
    `/survey/api/shopqr/start_session`,
    {
      href: window.location.href,
    }
  );

  return response.data as CxHttpRes;

});

// 回答結果を送信する
export const sendAnswer = createAsyncThunk<
  boolean,
  undefined,
  {
    dispatch: AppDispatch,
    state: RootState,
  }
>('inputAnswer/sendAnswer', async (_, { getState, dispatch }) => {

  const state = getState();

  // 二重送信を防ぐ
  if (state.inputAnswer.answer.status !== 'idle') {
    return true;
  }

  // 状態を「送信中」に変更
  dispatch(pendingSendAnswer());

  // Get the current branch which user chose
  const branchId = state.inputAnswer.selectedBranch?.id;

  const isCommonQR =
    state.inputAnswer.session.cxHttpRes?.data?.cxAppSession.shopqr?.token
      ?.isCommon;

  // 回答内容を送信
  const cxAnswerPages = state.inputAnswer.answer.cxAnswerPageDtos;

  // クライアント情報をセット
  let clientInfoProps = '';
  try {
    clientInfoProps = JSON.stringify(ClientInfoUtil.getProperties());
  } catch (error) {
    console.error(error);
  }

  let reqParams = { clientInfoProps, cxAnswerPages };
  if (isCommonQR && branchId) {
    reqParams = Object.assign(reqParams, { branchId });
  }
  const response = await axios.post(
    "/survey/api/shopqr/send_answer",
    reqParams
  );

  // ページ遷移（sideEffect）させたいので、Thunk内で全ての処理を終わらせる
  // return response.data as HttpRes;
  const payload = response.data as CxHttpRes;

  if (
    typeof payload === 'object' &&
    (payload as Object).hasOwnProperty('isError') &&
    !payload.isError
  ) {

    // 送信完了にする
    //state.inputAnswer.answer.status = 'fulfilled';
    dispatch(fulfilledSendAnswer());

    // 回答完了ページに遷移する
    dispatch(replace(appPath.shopqr.sentAnswer));

  } else {

    throw new Error(`invalid response`);

  }

  return true;
});

// thunk end
// ================================================================================================


const selectQuestion = (args: {
  state: InputAnswerState,
  pageIndex: number,
  questionIndex: number,
}): CxQuestion => {

  const {
    state,
    pageIndex,
    questionIndex,
  } = args;

  const cxQuestionPages = state.session.cxHttpRes?.data?.cxShopToken?.branch?.cxBrand?.cxQuestionHeaders[0]?.cxQuestionPages!;
  const cxQuestionPage = cxQuestionPages[pageIndex];
  const cxQuestion = cxQuestionPage.cxQuestions[questionIndex];

  return cxQuestion;
}

// 回答を取得する
const selectCxAnswer = (
  state: InputAnswerState,
  indexes: {
    pageIndex: number,
    questionIndex: number,
  }
) => {

  const {
    pageIndex,
    questionIndex,
  } = indexes;

  const cxAnswerPage = state.answer.cxAnswerPageDtos[pageIndex];
  const cxAnswer = cxAnswerPage.cxAnswers[questionIndex];

  return cxAnswer;
}

// 文字列を元に、回答項目の選択肢を取得する
export const selectCxAnswerSet = (
  cxAnswerSets: CxAnswerSet[],
  cxQuestionOption: CxQuestionOption,
) => {

  for (const cxAnswerSet of cxAnswerSets) {
    if (cxAnswerSet.id === cxQuestionOption.id) {
      return cxAnswerSet;
    }
  }

  throw new Error(`cxAnswerSet id=${cxQuestionOption.id} not found1`);
}

// 文字列を元に、回答項目の選択肢を取得する
const selectCxQuestionOptionByStr = (
  cxQuestion: CxQuestion,
  answerStr: string,
) => {

  for (const cxQuestionOption of cxQuestion.cxQuestionOptions) {
    if (cxQuestionOption.optionText === answerStr) {
      return cxQuestionOption;
    }
  }

  return null;
}

// 質問項目に対して正しく回答しているかチェックする
const answered = (args: {
  state: InputAnswerState,
  pageIndex: number,
  questionIndex: number,
}): boolean => {

  const {
    state,
    pageIndex,
    questionIndex
  } = args;

  const cxAnswer = selectCxAnswer(
    state,
    {
      pageIndex,
      questionIndex,
    }
  );

  const cxQuestion = selectQuestion({
    state,
    pageIndex,
    questionIndex,
  });

  if (cxAnswer.questionType === CxQuestionType.SELECT_SCORE) {

    // 必須でない場合、trueを返す
    if (!cxQuestion.required) {
      return true;
    }

    // 必須
    if (cxAnswer.answerScore === null) {
      return false;
    }
  } else if (
    cxAnswer.questionType === CxQuestionType.RADIO_BUTTON
  ) {

    // 未選択でないかチェック
    const notSelected = cxAnswer.answerStr === null || cxAnswer.answerStr.trim() === '';

    if (notSelected) {

      // 必須の場合falseを返す。非必須の場合、trueを返す
      if (cxQuestion.required) {
        return false;
      }

      return true;
    }

    // 選択済みの場合、選択されたオプションを取得する
    const cxQuestionOption = selectCxQuestionOptionByStr(cxQuestion, cxAnswer.answerStr!);

    if (!cxQuestionOption) {
      return true;
    }

    // フリーテキストなしの場合trueを返す
    if (!cxQuestionOption.withFreeText) {
      return true;
    }

    // フリーテキストが非必須の場合trueを返す
    if (!cxQuestionOption.freeTextRequired) {
      return true;
    }

    // フリーテキスト回答ずみの場合trueを返す
    if (cxAnswer.answerSubStr) {
      return true;
    }

    return false;

  } else if (
    cxAnswer.questionType === CxQuestionType.DROPDOWN ||
    cxAnswer.questionType === CxQuestionType.FREE_TEXT ||
    cxAnswer.questionType === CxQuestionType.CALENDAR ||
    false
  ) {

    // 必須でない場合、trueを返す
    if (!cxQuestion.required) {
      return true;
    }

    // 必須
    if (cxAnswer.answerStr === null || cxAnswer.answerStr.trim() === '') {
      return false;
    }
  } else if (
    cxAnswer.questionType === CxQuestionType.CHECKBOX
  ) {

    if (cxQuestion.required) {

      // 必須で選択数がゼロの場合、falseを返す
      let checkedCnt = 0;

      for (const cxAnswerSet of cxAnswer.cxAnswerSets) {
        if (cxAnswerSet.answerStr) {
          checkedCnt += 1;
        }
      }

      if (checkedCnt <= 0) {
        return false;
      }
    }

    // 個々の選択肢について、FreeTextがあるか？
    // FreeTextがある場合、必須か？
    // FreeTextが必須の場合、入力済みかをチェックする
    // ループの途中でreturnさせない
    let answered = true;
    for (const cxAnswerSet of cxAnswer.cxAnswerSets) {

      const cxQuestionOption = selectCxQuestionOptionByStr(
        cxQuestion,
        cxAnswerSet.answerStr,
      );

      if (cxQuestionOption) {
        // 自由テキストが必須か？
        if (
          cxQuestionOption.withFreeText &&
          cxQuestionOption.freeTextRequired
        ) {
          // 自由テキスト回答済みか？
          if (!cxAnswerSet.answerSubStr) {
            answered = false;
          }
        }
      }
    }

    return answered;

  } else if (
    cxAnswer.questionType === CxQuestionType.ZIPCODE
  ) {

    // 未入力の場合、必須の場合はfalse,非必須の場合はtrueを返す
    if (!cxAnswer.answerStr) {
      if (cxQuestion.required) {
        return false;
      }
      return true;
    }

    // 入力値が郵便番号として正しいかチェック
    if (ZIPCODE_REGEX.test(cxAnswer.answerStr)) {
      return true;
    }

    return false;

  } else {
    // N/A
    AssertUtil.unreachable(cxAnswer.questionType);
  }

  return true;
}

// ページ内の必須項目に全て回答したかチェック
export const pageAnswered = (args: {
  state: InputAnswerState,
  pageIndex: number,
}): boolean => {

  const {
    state,
    pageIndex,
  } = args;

  if (typeof pageIndex !== 'number') {
    return false;
  }

  if (pageIndex < 0) {
    return false;
  }

  const cxAnswerPage = state.answer.cxAnswerPageDtos[pageIndex];

  for (let answerIndex = 0; answerIndex < cxAnswerPage.cxAnswers.length; answerIndex += 1) {

    if (!answered({
      state,
      pageIndex,
      questionIndex: answerIndex,
    })) {
      return false;
    }
  }

  return true;
}

const startShopqrServerSession = (args: {
  state: InputAnswerState,
  cxHttpRes: CxHttpRes,
}): void => {

  const {
    state,
    cxHttpRes,
  } = args;

  state.session.cxHttpRes = cxHttpRes;

  if (cxHttpRes.data?.cxAppSession.shopqr?.survey.answered) {
    state.answer.status = 'fulfilled';
  }

  if (
    cxHttpRes.data &&
    cxHttpRes.data.cxShopToken &&
    cxHttpRes.data.cxShopToken.branch &&
    cxHttpRes.data.cxShopToken.branch.cxBrand &&
    cxHttpRes.data.cxShopToken.branch.cxBrand.cxQuestionHeaders[0] &&
    cxHttpRes.data.cxShopToken.branch.cxBrand.cxQuestionHeaders[0].cxQuestionPages &&
    true
  ) {

    const cxQuestionHeader = cxHttpRes.data.cxShopToken.branch.cxBrand.cxQuestionHeaders[0];
    const cxQuestionPages = cxQuestionHeader.cxQuestionPages;

    if (cxQuestionPages.length > 0) {

      state.session.httpResIsValid = true;

      // 回答結果を格納する変数を作成
      state.answer.cxAnswerPageDtos = cxQuestionPages.map((cxQuestionPage, index) => {

        return {
          cxQuestionPageId: cxQuestionPage.id!,
          answered: false,
          cxAnswers: cxQuestionPage.cxQuestions.map((cxQuestion, index) => {

            let answerStr: string | null;

            switch (cxQuestion.questionType) {
              case CxQuestionType.FREE_TEXT:
              case CxQuestionType.ZIPCODE:
              case CxQuestionType.CALENDAR:
                answerStr = ``;
                break;
              case CxQuestionType.CHECKBOX:
              case CxQuestionType.DROPDOWN:
              case CxQuestionType.RADIO_BUTTON:
              case CxQuestionType.SELECT_SCORE:
                answerStr = null;
                break;
              default:
                AssertUtil.unreachable(cxQuestion.questionType);
            }
            // const answerStr = question.questionType === QuestionType.FREE_TEXT ? '' : null;

            const cxAnswerSets: CxAnswerSet[] = [];

            for (const cxQuestionOption of cxQuestion.cxQuestionOptions) {

              const cxAnswerSet: CxAnswerSet = {
                id: cxQuestionOption.id!,
                answerStr: '',
                answerSubStr: '',
              }

              cxAnswerSets.push(cxAnswerSet);
            }

            const cxAnswerDto: CxAnswerDto = {
              cxQuestionId: cxQuestion.id!,
              questionType: cxQuestion.questionType,
              answerStr,
              answerSubStr: '',
              cxAnswerSets,
              answerScore: null,
            };

            return cxAnswerDto;
          }),
          clientInfo: '',
        }
      })

      // ゲーム情報を作成(テスト用)
      if (state.session.cxHttpRes!.data?.cxShopToken!.id === -100) {

        state.session.couponInStock = true;

        state.couponState = {
          registerEmailStatus: 'idle',
          issueCouponStatus: 'idle',
          useCouponStatus: 'idle',
        }

        if (state.session.cxHttpRes!.data.cxAppSession.shopqr!.registeredEmail) {
          state.couponState!.registerEmailStatus = 'fulfilled';
        }
      }
    }
  }
}

const startCouponServerSession = (args: {
  state: InputAnswerState,
  cxHttpRes: CxHttpRes,
}): void => {

  const {
    state,
    cxHttpRes,
  } = args;

  state.session.cxHttpRes = cxHttpRes;
  state.session.httpResIsValid = true;

}

export const inputAnswerSlice = createSlice({
  name: 'inputAnswer',
  initialState,
  reducers: {
    // selectCorrectAnswer: (
    //   state,
    //   action: PayloadAction<{
    //     questionIndex: number,
    //   }>,
    // ) => {
    //   state.game.answers[action.payload.questionIndex].cleared = true;
    // },
    resetScore: (
      state,
      action: PayloadAction<{
        pageIndex: number,
        questionIndex: number,
      }>,
    ) => {

      const cxAnswer = selectCxAnswer(state, action.payload);
      cxAnswer.answerScore = null;
    },
    selectScore: (
      state,
      action: PayloadAction<{
        pageIndex: number,
        questionIndex: number,
        score: CX_SCORE_VAL,
      }>,
    ) => {

      const cxAnswer = selectCxAnswer(state, action.payload);
      cxAnswer.answerScore = action.payload.score;
    },
    changeFreeText: (
      state,
      action: PayloadAction<{
        pageIndex: number,
        questionIndex: number,
        str: string,
      }>,
    ) => {

      const cxAnswer = selectCxAnswer(state, action.payload);
      cxAnswer.answerStr = action.payload.str;
    },
    changeCalendar: (
      state,
      action: PayloadAction<{
        pageIndex: number,
        questionIndex: number,
        str: string,
      }>,
    ) => {

      const cxAnswer = selectCxAnswer(state, action.payload);
      cxAnswer.answerStr = action.payload.str;
    },
    changeDropdown: (
      state,
      action: PayloadAction<{
        pageIndex: number,
        questionIndex: number,
        value: string,
      }>,
    ) => {

      const cxAnswer = selectCxAnswer(state, action.payload);
      cxAnswer.answerStr = action.payload.value;
    },
    changeRadioButton: (
      state,
      action: PayloadAction<{
        pageIndex: number,
        questionIndex: number,
        value: string,
      }>,
    ) => {

      const cxAnswer = selectCxAnswer(state, action.payload);

      if (cxAnswer.answerStr !== action.payload.value) {
        cxAnswer.answerStr = action.payload.value;
        cxAnswer.answerSubStr = '';
      }
    },
    changeRadioButtonFreeText: (
      state,
      action: PayloadAction<{
        pageIndex: number,
        questionIndex: number,
        freeText: string,
      }>,
    ) => {

      const cxAnswer = selectCxAnswer(state, action.payload);
      cxAnswer.answerSubStr = action.payload.freeText;
    },
    changeCheckBox: (
      state,
      action: PayloadAction<{
        pageIndex: number,
        questionIndex: number,
        cxQuestionOption: CxQuestionOption,
        value: string,
        checked: boolean,
      }>,
    ) => {

      const {
        checked,
        cxQuestionOption,
      } = action.payload;

      const cxAnswer = selectCxAnswer(state, action.payload);
      const cxAnswerSet = selectCxAnswerSet(cxAnswer.cxAnswerSets, cxQuestionOption)!;

      if (!checked) {
        // チェックが外された場合、対象回答データを削除する
        cxAnswerSet.answerStr = '';
        cxAnswerSet.answerSubStr = '';
      } else {
        // チェックがつけられた場合
        // 値がセットされていない場合、値をセットする
        if (cxAnswerSet.answerStr !== cxQuestionOption.optionText) {
          cxAnswerSet.answerStr = cxQuestionOption.optionText;
          cxAnswerSet.answerSubStr = '';
        }
      }
    },
    changeCheckBoxFreeText: (
      state,
      action: PayloadAction<{
        pageIndex: number,
        questionIndex: number,
        cxQuestionOption: CxQuestionOption,
        freeText: string,
      }>,
    ) => {

      const {
        cxQuestionOption,
        freeText,
      } = action.payload;

      const cxAnswer = selectCxAnswer(state, action.payload);
      const cxAnswerSet = selectCxAnswerSet(cxAnswer.cxAnswerSets, cxQuestionOption)!;

      cxAnswerSet.answerSubStr = freeText;
    },
    answeredToPage: (
      state,
      action: PayloadAction<{
        pageIndex: number,
      }>,
    ) => {

      const pageIndex = action.payload.pageIndex;
      state.answer.cxAnswerPageDtos[pageIndex].answered = true;
    },
    changeErrorCount: (
      state,
      action: PayloadAction<{
        errorCount: number;
      }>) =>  {
      state.errorCount = action.payload.errorCount
    },
    pendingSendAnswer: (
      state,
    ) => {
      state.answer.status = 'pending';
    },
    fulfilledSendAnswer: (
      state,
    ) => {
      state.answer.status = 'fulfilled';
    },
    registerEmailStarted: (
      state,
    ) => {

      if (state.couponState) {
        state.couponState.registerEmailStatus = 'pending';
      }
    },
    registerEmailRejected: (
      state,
    ) => {

      if (state.couponState) {
        state.couponState.registerEmailStatus = 'rejected';
      }
    },
    registerEmailFulfilled: (
      state,
    ) => {

      if (state.couponState) {
        state.couponState.registerEmailStatus = 'fulfilled';
      }
    },
    issueCouponStarted: (state) => {
      state.couponState!.issueCouponStatus = 'pending';
    },
    issueCouponRejected: (state) => {
      state.couponState!.issueCouponStatus = 'rejected';
    },
    issueCouponFulfilled: (
      state,
      action: PayloadAction<{
        cxHttpRes: CxHttpRes,
      }>,
    ) => {

      state.couponState!.issueCouponStatus = 'fulfilled';

      startCouponServerSession({
        state,
        cxHttpRes: action.payload.cxHttpRes,
      })
    },
    xuseCouponStarted: (state) => {
      state.couponState!.useCouponStatus = 'pending';
    },
    xuseCouponRejected: (state) => {
      state.couponState!.useCouponStatus = 'rejected';
    },
    xuseCouponFulfilled: (
      state,
      action: PayloadAction<{
        cxHttpRes: CxHttpRes,
      }>,
    ) => {

      state.couponState!.useCouponStatus = 'fulfilled';
      state.session.cxHttpRes = action.payload.cxHttpRes;

    },
    changeCurrentBranch: (
      state,
      action: PayloadAction<{
        branch: BranchesRes | null;
      }>
    ) => {
      state.selectedBranch = action.payload.branch;
    },
  },
  extraReducers: (builder) => {

    // ====================================================
    // セッション開始
    builder.addCase(startServerSession.fulfilled, (state, action) => {

      state.session.status = 'fulfilled';

      const cxHttpRes = action.payload;

      state.session.httpResIsValid = false;

      // モードが店舗QRかクーポンかで場合分け
      switch (cxHttpRes.data?.cxAppSession.appMode) {
        case APP_MODE_V.shopqr:
          startShopqrServerSession({
            state,
            cxHttpRes,
          });
          break;

        case APP_MODE_V.coupon:
          startCouponServerSession({
            state,
            cxHttpRes,
          });
          break;

        default:
          console.error(`unknown appMode:[${cxHttpRes.data?.cxAppSession.appMode}]`);
          state.session.status = 'rejected';
          break;
      }

    });

    builder.addCase(startServerSession.rejected, (state, action) => {

      console.error(action);
      state.session.status = 'rejected';
    });
    // セッション開始
    // ====================================================

    // ====================================================
    // 回答送信
    builder.addCase(sendAnswer.fulfilled, (state, action) => {

      // ページ遷移（sideEffect）させたいので、Thunk内で全ての処理を終わらせる
      // 二重送信の場合は処理を抜ける
      // if (action.payload === null) {
      //   return;
      // }

      // 正常に終了している場合、送信完了にする
      // if (
      //   typeof action.payload === 'object' &&
      //   (action.payload as Object).hasOwnProperty('isError') &&
      //   !action.payload.isError
      // ) {

      //   // 送信完了にする
      //   state.answer.status = 'fulfilled';

      // } else {

      //   state.answer.status = 'rejected';

      // }
    });

    builder.addCase(sendAnswer.rejected, (state, action) => {

      console.error(action);
      state.answer.status = 'rejected';
    });
    // 回答送信
    // ====================================================

  },
})

export const {
  // selectCorrectAnswer,
  resetScore,
  selectScore,
  changeFreeText,
  changeCalendar,
  changeDropdown,
  changeRadioButton,
  changeRadioButtonFreeText,
  changeCheckBox,
  changeCheckBoxFreeText,
  answeredToPage,
  changeErrorCount,
  pendingSendAnswer,
  fulfilledSendAnswer,
  registerEmailStarted,
  registerEmailRejected,
  registerEmailFulfilled,
  issueCouponStarted,
  issueCouponRejected,
  issueCouponFulfilled,
  xuseCouponStarted,
  xuseCouponRejected,
  xuseCouponFulfilled,
  changeCurrentBranch,
} = inputAnswerSlice.actions

export const inputAnswerReducer = inputAnswerSlice.reducer
